import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { isAxiosError } from "axios";
import { profileClient } from "../../api/clients";
import { POSITIONS } from "../../shared/constants/userConstants";
import { OfficerProfile } from "../../shared/interfaces/OfficerProfile";
import { ProfileResponse } from "../../shared/interfaces/ProfileRespone";

interface ProfileMetadataFormProps {
  officer: OfficerProfile;
  onSaved: (updated: OfficerProfile) => void;
}

const BADGE_NUMBER_PATTERN = /^\d{1,10}$/;

const ProfileMetadataForm: React.FC<ProfileMetadataFormProps> = ({
  officer,
  onSaved,
}) => {
  const [badgeNumber, setBadgeNumber] = useState(officer.badgeNumber ?? "");
  const [position, setPosition] = useState(officer.rank ?? "");
  const [department, setDepartment] = useState(officer.department ?? "");
  const [submitting, setSubmitting] = useState(false);
  // Synchronous in-flight guard: setSubmitting is async, so rapid clicks can
  // re-enter handleSave before the disabled state has actually re-rendered.
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setBadgeNumber(officer.badgeNumber ?? "");
    setPosition(officer.rank ?? "");
    setDepartment(officer.department ?? "");
    setError(null);
    setSuccess(null);
  }, [officer]);

  const isDirty = useMemo(
    () =>
      badgeNumber.trim() !== (officer.badgeNumber ?? "") ||
      position !== (officer.rank ?? "") ||
      department.trim() !== (officer.department ?? ""),
    [badgeNumber, position, department, officer],
  );

  const validate = (): string | null => {
    if (!badgeNumber.trim()) return "Badge number is required.";
    if (!BADGE_NUMBER_PATTERN.test(badgeNumber.trim())) {
      return "Badge number must be 1-10 digits.";
    }
    if (!position) return "Job title is required.";
    if (!department.trim()) return "Department is required.";
    return null;
  };

  const handleSave = async () => {
    if (submittingRef.current) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      setSuccess(null);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await profileClient.put<ProfileResponse>(
        "/v1/profile",
        {
          email: officer.email,
          firstName: officer.firstName,
          lastName: officer.lastName,
          divisionName: department.trim(),
          jobTitle: position,
          badgeNumber: badgeNumber.trim(),
        },
      );
      const { user, memberships } = response.data;
      const updated: OfficerProfile = {
        ...officer,
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        department: user.metadata.department,
        rank: user.metadata.position,
        badgeNumber: user.metadata.badge_number,
        stationName: memberships[0]?.organizationName ?? officer.stationName,
        status: memberships[0]?.status ?? officer.status,
      };
      onSaved(updated);
      setSuccess("Profile details updated.");
    } catch (e) {
      const message = isAxiosError(e) ? e.response?.data?.error : null;
      setError(message || "Failed to update profile. Please try again.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setBadgeNumber(officer.badgeNumber ?? "");
    setPosition(officer.rank ?? "");
    setDepartment(officer.department ?? "");
    setError(null);
    setSuccess(null);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Department Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update your badge number, job title, and department.
        </Typography>
      </Box>

      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Badge Number"
              value={badgeNumber}
              onChange={(e) => setBadgeNumber(e.target.value)}
              required
              fullWidth
              disabled={submitting}
              helperText="1-10 digits"
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required disabled={submitting}>
              <InputLabel id="profile-rank-label">Job Title</InputLabel>
              <Select
                labelId="profile-rank-label"
                label="Job Title"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
              >
                {POSITIONS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              required
              fullWidth
              disabled={submitting}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            onClick={handleReset}
            disabled={submitting || !isDirty}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Reset
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={submitting || !isDirty}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            {submitting ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default ProfileMetadataForm;
